import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPhoneNumber(phone: string): string {
  return phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
}

export function formatDate(date: string | Date | number[] | number, includeTime = false): string {
  if (!date) return '-';
  
  // Handle array format [년, 월, 일, 시, 분, 초] or [년, 월, 일, 시, 분]
  if (Array.isArray(date)) {
    if (date.length < 3) return '-';
    
    const [year, month, day, hour = 0, minute = 0, second = 0] = date;
    
    // Validate year, month, day
    if (!year || !month || !day) return '-';
    if (month < 1 || month > 12) return '-';
    if (day < 1 || day > 31) return '-';
    
    // Create date object (month is 0-based in JavaScript Date)
    const dateObj = new Date(year, month - 1, day, hour, minute, second);
    
    // Validate the date
    if (isNaN(dateObj.getTime())) return '-';
    
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(includeTime && { hour: '2-digit', minute: '2-digit' })
    }).format(dateObj);
  }
  
  // Handle timestamp (number)
  if (typeof date === 'number') {
    const timestamp = date.toString().length <= 10 ? date * 1000 : date;
    const dateObj = new Date(timestamp);
    
    if (isNaN(dateObj.getTime())) return '-';
    
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(includeTime && { hour: '2-digit', minute: '2-digit' })
    }).format(dateObj);
  }
  
  // Handle string or Date object
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(includeTime && { hour: '2-digit', minute: '2-digit' })
  }).format(new Date(date));
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    normal: 'text-success bg-success/10',
    partial_error: 'text-warning bg-warning/10',
    error: 'text-danger bg-danger/10',
    logged_in: 'text-success bg-success/10',
    logged_out: 'text-warning bg-warning/10',
    blocked: 'text-danger bg-danger/10',
    pending: 'text-warning bg-warning/10',
    in_progress: 'text-primary bg-primary/10',
    completed: 'text-success bg-success/10',
    failed: 'text-danger bg-danger/10',
  };
  
  return statusColors[status] || 'text-gray-500 bg-gray-100';
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), delay);
    }
  };
}